import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import type { ReactNode } from "react";

/**
 * A storefront value an environment inherits from the platform until an editor customizes it:
 * one card while it follows the platform default (what that default is, and a way to start
 * customizing), another while it is the environment's own (the form, and a way back). Every
 * such value's form already renders its buttons in one row, so the "Use platform default"
 * button is handed to the caller to place as the form's `footer` rather than rendered here.
 *
 * The page keeps `customizing` and the start value: whether "Customize" begins from the
 * platform's value (shipping, the loser prize, the banner theme) or from nothing (the legal
 * identity, the Termly website) is the page's decision, and its mutation is what resets the
 * flag after a save.
 */
export function InheritOrCustomize({
  title,
  inherited,
  customizing,
  canEdit,
  saving,
  onCustomize,
  onCancel,
  onRevert,
  descriptions,
  inheritedSummary,
  children,
}: {
  /** Both cards' title when a page holds two pairs; defaults to naming whose value it is. */
  title?: string;
  /** The environment's own value is null: it follows the platform default. */
  inherited: boolean;
  /** An inherited value is being edited; nothing is saved yet. */
  customizing: boolean;
  canEdit: boolean;
  saving: boolean;
  onCustomize: () => void;
  /** Stop customizing an inherited value — there is nothing to save. */
  onCancel: () => void;
  /** Drop the saved override, so the platform default applies again. */
  onRevert: () => void;
  descriptions: { inherited: ReactNode; customizing: ReactNode; customized: ReactNode };
  /** What the platform default looks like: a table, a preview, a summary list. */
  inheritedSummary: ReactNode;
  /** The form, keyed by the caller; `revert` goes in its `footer` (null for a viewer). */
  children: (revert: ReactNode) => ReactNode;
}) {
  if (inherited && !customizing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {title ?? "Platform default"} <Badge variant="secondary">inherited</Badge>
          </CardTitle>
          <CardDescription>{descriptions.inherited}</CardDescription>
          {canEdit && (
            <CardAction>
              <Button type="button" variant="outline" onClick={onCustomize}>
                Customize for this environment
              </Button>
            </CardAction>
          )}
        </CardHeader>
        <CardContent>{inheritedSummary}</CardContent>
      </Card>
    );
  }

  const revert = canEdit ? (
    <Button
      type="button"
      variant="outline"
      disabled={saving}
      onClick={inherited ? onCancel : onRevert}
    >
      Use platform default
    </Button>
  ) : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {title ?? "This environment"}{" "}
          <Badge variant={inherited ? "outline" : "default"}>
            {inherited ? "customizing" : "customized"}
          </Badge>
        </CardTitle>
        <CardDescription>
          {inherited ? descriptions.customizing : descriptions.customized}
        </CardDescription>
      </CardHeader>
      <CardContent className="gap-4">{children(revert)}</CardContent>
    </Card>
  );
}
